// import MarketUtility from '../../utils/marketUtility';
import moment from 'moment';
import { PRETTIER_FORMAT_WITH_SECONDS, TIME_FORMAT } from '@javascript/habitual-analytics/dateUtils/dateFormats';
import {
  sanitizeAndParseOrderStatus,
  sanitizeParseOrderType,
  sanitizeProductType,
  formatUTCToIST,
  sanitizeDefaultOrderType,
  sanitizeTransActionType
} from '@javascript/habitual-analytics/brokers/OrderFacade/parser/parseHelper';
import {
  ParseTradingSymbol
} from '@javascript/habitual-analytics/brokers/OrderFacade/parser/instrumentParser';
import { isValidSymbolForInsta } from '@javascript/habitual-analytics/brokers/utils';
import {
  OrderResult,
} from '@javascript/habitual-analytics/brokers/OrderFacade/types/facade';
import { BrokerOrderId, SuccessResult, ErrorResult, Segments, Exchanges } from '@javascript/habitual-analytics/brokers/OrderFacade/types/common';
import MarketUtility from '@javascript/habitual-analytics/utils/marketUtility';
import _ from 'lodash';
import { getCancellableOrderStatus } from '@javascript/habitual-analytics/brokers/utils';
import { Facade } from '@javascript/habitual-analytics/brokers/OrderFacade/types/facade';
import { Insta } from '@javascript/habitual-analytics/brokers/OrderFacade/types/insta';

export type TransactionResponse = Facade.PlaceOrderResult
  | Facade.CancelOrderResult
  | Facade.ModifyOrderResult;

type ParseTransaction = SuccessResult<{ orderId: BrokerOrderId }>
  | (ErrorResult & { message: string });

export const Parser = (() => {
  return {
    parseOrderBook: (rawItem: Facade.OrderBook):
      Insta.OrderBook | null => {
      const { tradingSymbol } = rawItem;
      // If Some BSE EQ order is placed, then ignore it, CleanUp this if Mandatory
      if (tradingSymbol.segment === Segments.EQ && tradingSymbol.exchange === Exchanges.BSE) {
        return null;
      };

      const tradingSymbolObj = ParseTradingSymbol(tradingSymbol);
      if (!isValidSymbolForInsta(tradingSymbolObj.getExchangeSegment(), tradingSymbolObj.toString())
        || !_.includes(MarketUtility.getSymbols(), tradingSymbolObj?.symbol)) {
        return null;
      }
      const status = sanitizeAndParseOrderStatus(rawItem.status);
      const lotSize = _.get(rawItem, 'lotSize', 1);
      const tradedQuantity = _.get(rawItem, 'filledQuantity', 0) / lotSize;
      const quantity = _.get(rawItem, 'quantity', 0) / lotSize;
      const isCancellableOrder = getCancellableOrderStatus(status);
      const productType = sanitizeParseOrderType(rawItem.orderType);
      const productCode = sanitizeProductType(rawItem.productType, tradingSymbolObj);
      const utcToIst = formatUTCToIST(rawItem.orderTimeStamp);

      return {
        tradingSymbolObj,
        type: sanitizeTransActionType(rawItem.transactionType),
        time: moment(utcToIst.split(' ')[1], TIME_FORMAT)
          ?.format(PRETTIER_FORMAT_WITH_SECONDS),
        status: status,
        isCancellableOrder,
        stopPrice: _.get(rawItem, 'triggerPrice', 0),
        limitPrice: _.get(rawItem, 'limitPrice', 0),
        failedReason: _.get(rawItem, 'message', '').toString(),
        extraDetails: {
          product: `${productType} / ${productCode}`,
          qty: `${tradedQuantity} / ${quantity}`,
          orderNo: _.get(rawItem, 'brokerOrderId', '').toString(),
          tradedPrice: _.get(rawItem, 'tradedPrice', 0),
          defaultProductType: sanitizeDefaultOrderType(rawItem.orderType),
          defaultProductCode: productCode,
        },
      };
    },

    parseTradeBook: (rawItem: Facade.TradeBook):
      Insta.TradeBook | null => {
      const tradingSymbolObj = ParseTradingSymbol(rawItem.tradingSymbol);
      if (!isValidSymbolForInsta(tradingSymbolObj.getExchangeSegment(), tradingSymbolObj.toString())
        || !_.includes(MarketUtility.getSymbols(), tradingSymbolObj?.symbol)) {
        return null;
      }
      const productCode = sanitizeProductType(rawItem.productType, tradingSymbolObj);
      const productType = sanitizeParseOrderType(rawItem.orderType);
      const lotSize = _.get(rawItem, 'lotSize', 1);
      const quantity = _.get(rawItem, 'quantity', 0) / lotSize;
      const utcToIst = formatUTCToIST(rawItem.orderTimeStamp);

      return {
        tradingSymbolObj,
        time: moment(utcToIst.split(' ')[1], TIME_FORMAT)
          ?.format(PRETTIER_FORMAT_WITH_SECONDS),
        type: sanitizeTransActionType(rawItem.transactionType),
        status: sanitizeAndParseOrderStatus(rawItem.status),
        quantity,
        extraDetails: {
          product: `${productCode} / ${productType}`,
          qty: `${quantity} / ${quantity}`,
          tradedPrice: _.get(rawItem, 'tradedPrice', 0),
        },
      };
    },

    parsePositionBook: (rawItem: Facade.PositionBook):
      Insta.PositionBook | null => {
      const tradingSymbolObj = ParseTradingSymbol(rawItem.tradingSymbol);

      if (!isValidSymbolForInsta(tradingSymbolObj.getExchangeSegment(), tradingSymbolObj.toString())
        || !_.includes(MarketUtility.getSymbols(), tradingSymbolObj?.symbol)) {
        return null;
      }
      const qty = _.get(rawItem, 'quantity', 0);

      return {
        tradingSymbolObj,
        type: sanitizeTransActionType(rawItem.transactionType),
        qty,
        buyAvg: Number(_.get(rawItem, 'buyAveragePrice', 0)),
        sellAvg: Number(_.get(rawItem, 'sellAveragePrice', 0)),
        ltp: _.get(rawItem, 'ltp', 0),
        buyQuantity: _.get(rawItem, 'buyQuantity', 0),
        sellQuantity: _.get(rawItem, 'sellQuantity', 0),
        avgPrice: _.get(rawItem, 'avgPrice', 0),
        profitLoss: _.get(rawItem, 'profitLoss', 0),
        realizedProfitLoss: _.get(rawItem, 'realizedProfitLoss', 0),
        extraDetails: {
          product: sanitizeProductType(rawItem.productType, tradingSymbolObj),
          isOpenPosition: _.get(rawItem, 'isOpenPosition', false),
          type: qty !== 0 ? sanitizeTransActionType(rawItem.transactionType) : '',
          defaultProductCode: sanitizeProductType(
            rawItem.productType, tradingSymbolObj),
        },
      };
    },

    parseHoldingBook: (rawItem: Facade.HoldingBook):
      Insta.HoldingBook | null => {
      const tradingSymbolObj = ParseTradingSymbol(rawItem.tradingSymbol);
      if (!isValidSymbolForInsta(tradingSymbolObj.getExchangeSegment(), tradingSymbolObj.toString())
        || !_.includes(MarketUtility.getSymbols(), tradingSymbolObj?.symbol)) {
        return null;
      }

      return {
        tradingSymbolObj: tradingSymbolObj,
        ltp: _.get(rawItem, 'ltp', 0),
        profitLoss: _.get(rawItem, 'profitLoss', 0),
        extraDetails: {
          quantity: _.get(rawItem, 'quantity', 0),
          buyAverage: _.get(rawItem, 'buyAveragePrice', 0),
          sellAverage: _.get(rawItem, 'sellAveragePrice', 0),
          buyValue: _.get(rawItem, 'buyValue', 0),
          averagePrice: _.get(rawItem, 'averagePrice', 0),
          netChg: _.get(rawItem, 'netChange', 0),

        },
      };
    },

    parsePostBackOrder: (rawItem: Facade.Postback):
      Insta.Postback => {
      const tradingSymbolObj = ParseTradingSymbol(rawItem.tradingSymbol);
      return {
        tradingSymbolObj,
        status: sanitizeAndParseOrderStatus(rawItem.status),
        failedReason: _.get(rawItem, 'message', '').toString(),
        extraDetails: {
          orderNo: _.get(rawItem, 'brokerOrderId', '').toString(),
        },
      };
    },

    parseTransactionResponse: (transactionResponse: OrderResult):
      ParseTransaction[] => {
      return _.values(transactionResponse).map((item) => {
        if (!item.success) {
          return { success: false, message: item.error?.reason || item.error?.message || 'order failed' };
        }
        return { success: true, orderId: item.brokerOrderId };
      });
    }
  };
});


